import { useEffect } from 'react'
import {
  VStack,
  Button,
  Grid,
  FormControl,
  FormLabel,
  Input,
  CheckboxGroup,
  Checkbox,
  Textarea,
  Link,
  useToast,
} from '@chakra-ui/react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation, Trans } from 'next-i18next'

import {
  DEFAULT_TOAST_OPTIONS,
  getErrorToastObject,
} from '@/lib/utils/format/toast'
import { PRIVACY_POLICY, TERMS_OF_USE } from '@/routes'
import requests from '@/lib/requests'
import env from '@/lib/env'
import { useUTMContext } from '@/lib/gtm'

import { SERVICE_OPTIONS } from './constants'
import { schema, FormValue } from './schema'

const defaultValues = {
  firstName: '',
  lastName: '',
  email: '',
  tel: '',
  additionalDetail: '',
  acceptedTerm: false,
  agreedToMarketingConsent: false,
  interestedProducts: [],
}

interface Props {
  onSuccess?: () => void
}

function ContactForm({ onSuccess }: Props): React.ReactElement {
  const {
    t: tCommon,
    i18n: { language },
  } = useTranslation('common')
  const { t: tOtherServices } = useTranslation('otherServices')

  const toast = useToast()
  const { data: utm } = useUTMContext()

  const {
    register,
    handleSubmit,
    reset: resetForm,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultValues,
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormValue) => {
    if (isSubmitting) return

    try {
      await requests.post(
        `${env.APP_URL}/api/other-services/contact`,
        { ...data, utm, language },
        {},
      )
      resetForm()

      toast({
        ...DEFAULT_TOAST_OPTIONS,
        description: tOtherServices('contact.form.successToast'),
      })

      onSuccess && onSuccess()
    } catch (error: any) {
      toast(getErrorToastObject(error))
    }
  }

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      toast({
        ...DEFAULT_TOAST_OPTIONS,
        status: 'error',
        description: tCommon('errors.incorrectData'),
      })
    }
  }, [errors])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack
        spacing={{ base: 7, sm: 12, md: 16 }}
        sx={{
          alignItems: 'stretch',
        }}
      >
        <Grid
          sx={{
            gridTemplateColumns: {
              base: '1fr',
              sm: 'repeat(2, 1fr)',
            },
            gridGap: 6,
          }}
        >
          <FormControl isRequired>
            <FormLabel>
              {tOtherServices('contact.form.firstName.label')}
            </FormLabel>
            <Input
              placeholder={tOtherServices('contact.form.firstName.placeholder')}
              {...register('firstName')}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>
              {tOtherServices('contact.form.lastName.label')}
            </FormLabel>
            <Input
              placeholder={tOtherServices('contact.form.lastName.placeholder')}
              {...register('lastName')}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>{tOtherServices('contact.form.email.label')}</FormLabel>
            <Input
              placeholder={tOtherServices('contact.form.email.placeholder')}
              type="email"
              {...register('email')}
            />
          </FormControl>
          <FormControl>
            <FormLabel>{tOtherServices('contact.form.tel.label')}</FormLabel>
            <Input
              placeholder={tOtherServices('contact.form.tel.placeholder')}
              type="tel"
              {...register('tel')}
            />
          </FormControl>
          <Controller
            name="interestedProducts"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <FormControl sx={{ gridColumn: { sm: '1 / 3' } }}>
                  <FormLabel>
                    {tOtherServices('contact.form.services.label')}
                  </FormLabel>
                  <CheckboxGroup onChange={onChange} value={value}>
                    <Grid
                      sx={{
                        gridTemplateColumns: {
                          base: '1fr',
                          sm: 'repeat(2, 1fr)',
                        },
                        gridGap: '12px 24px',
                        fontWeight: 'medium',
                        justifyItems: 'flex-start',
                      }}
                    >
                      {SERVICE_OPTIONS.map((option, index) => {
                        return (
                          <Checkbox
                            key={`${index}`}
                            value={option}
                            sx={{
                              alignItems: 'flex-start',
                              span: { fontSize: 'md' },
                            }}
                          >
                            {option}
                          </Checkbox>
                        )
                      })}
                    </Grid>
                  </CheckboxGroup>
                </FormControl>
              )
            }}
          />
          <FormControl sx={{ gridColumn: { sm: '1 / 3' } }}>
            <FormLabel>
              {tOtherServices('contact.form.details.label')}
            </FormLabel>
            <Textarea
              placeholder={tOtherServices('contact.form.details.placeholder')}
              sx={{ height: 28 }}
              {...register('additionalDetail')}
            />
          </FormControl>
          <VStack
            spacing="4"
            sx={{
              alignItems: 'flex-start',
              fontWeight: 'medium',
              gridColumn: { sm: '1 / 3' },
            }}
          >
            <Controller
              name="acceptedTerm"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Checkbox
                    isRequired
                    onChange={onChange}
                    isChecked={value}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <Trans
                      t={tOtherServices}
                      i18nKey="contact.form.agreedToTerms"
                    >
                      ฉันยอมรับ
                      <Link
                        isExternal
                        sx={{ textDecoration: 'underline !important' }}
                        href={`/${language}${TERMS_OF_USE.pathname}`}
                      >
                        เงื่อนไขในการใช้เว็บไซต์
                      </Link>
                      และ
                      <Link
                        isExternal
                        sx={{ textDecoration: 'underline !important' }}
                        href={`/${language}${PRIVACY_POLICY.pathname}`}
                      >
                        นโยบายความเป็นส่วนตัว
                      </Link>
                    </Trans>
                  </Checkbox>
                )
              }}
            />
            <Controller
              name="agreedToMarketingConsent"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Checkbox
                    onChange={onChange}
                    isChecked={value}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <Trans t={tCommon} i18nKey="contact.form.marketingConsent">
                      ฉันต้องการรับโปรโมชันและข่าวสารทางการตลาดเกี่ยวกับ&nbsp;
                      PDPA Core &nbsp;รวมถึงบริการอื่น ๆ ที่เกี่ยวข้องกับ PDPA <br />
                      (คุณสามารถยกเลิกได้ทุกเมื่อ)
                    </Trans>
                  </Checkbox>
                )
              }}
            />
          </VStack>
        </Grid>
        <Button
          isLoading={isSubmitting}
          type="submit"
          size="xl"
          colorScheme="primary"
          sx={{
            alignSelf: 'center',
            w: 'fit-content',
            mx: 'auto',
          }}
        >
          {tOtherServices('contact.form.submitButton')}
        </Button>
      </VStack>
    </form>
  )
}

export default ContactForm
